/**
 * F-180 (B1) — ProductOffering REST wrapper.
 *
 * Mirrors `presets.ts` shape. Multipart-aware: `createProduct` and
 * `updateProduct` accept an optional `image` File and switch to FormData
 * automatically so the backend can run its image upload pipeline.
 */
import { api } from './client';
import type { ProductDeliveryMode, ProductOffering } from '@/types/domain';

export interface ProductInput {
  category_slug: string;
  title: string;
  description: string;
  price_eur: number;
  price_is_from?: boolean;
  /** null / omitted = unlimited stock per backend ProductOffering contract. */
  stock?: number | null;
  is_active?: boolean;
  delivery_modes?: ProductDeliveryMode[];
  delivery_fee_eur?: number | null;
  /** Optional image file. When present the request is sent as multipart. */
  image?: File | null;
}

function toFormData(input: Partial<ProductInput>): FormData {
  const form = new FormData();
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined || value === null) continue;
    if (key === 'image' && value instanceof File) {
      form.append('image', value);
      continue;
    }
    if (key === 'delivery_modes' && Array.isArray(value)) {
      // Backend `_clean_delivery_modes` expects a list; DRF parses repeated
      // form keys into a list when using MultiPartParser.
      for (const mode of value) form.append('delivery_modes', String(mode));
      continue;
    }
    if (typeof value === 'boolean') {
      form.append(key, value ? 'true' : 'false');
      continue;
    }
    form.append(key, String(value));
  }
  return form;
}

export async function listMyProducts(): Promise<{ results: ProductOffering[] }> {
  return api.get('/products/me/');
}

export async function listPublic(
  specialistSlug?: string,
): Promise<{ results: ProductOffering[] }> {
  if (!specialistSlug) {
    // No global product feed yet — backend only exposes the per-specialist
    // public endpoint. Return an empty payload so callers stay simple.
    return { results: [] };
  }
  return api.get(`/products/by-specialist/${specialistSlug}/`);
}

export async function createProduct(input: ProductInput): Promise<ProductOffering> {
  return api.post('/products/me/', { body: toFormData(input) });
}

export async function updateProduct(
  id: number,
  input: Partial<ProductInput>,
): Promise<ProductOffering> {
  return api.patch(`/products/me/${id}/`, { body: toFormData(input) });
}

export async function deleteProduct(id: number): Promise<void> {
  await api.del(`/products/me/${id}/`);
}
